import { Declaration } from 'store/declarations/declaration';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { AISMessageTypes } from 'store/declarations/enums/common/declaration-types';
import { ItemErrors, MirroringPreparationLocationState, SourceDeclarationItems } from './types';
import { validateYupSchema, yupToFormErrors } from 'formik';
import IrelandImportH1DeclarationUtils from 'views/declarations/ireland/import/h1/utils';
import { irelandH1DisplayedFieldsValidationSchema } from './issues-step/fix-item-modal/irelandH1ItemFieldsRegistry';

// Packaging is a list, and Yup's `array().of(...)` only runs on elements that exist - an item
// with no packaging entry would silently pass the packaging validators instead of reporting them
// as missing. Seeding one empty entry makes the element-level validators run, so the errors land
// on the same `packaging.0.*` paths the table, the checklist and the quick-fix modal read from.
// The quick-fix modal seeds the same way, since it only ever edits the first entry.
export const withSeededIrelandH1Packaging = (item: any) => ({
    ...item,
    goodsInformation: {
        ...item?.goodsInformation,
        packaging: item?.goodsInformation?.packaging?.length ? item.goodsInformation.packaging : [{}],
    },
});

export const withSeededUkPackaging = (item: any) => ({
    ...item,
    packaging: item?.packaging?.length ? item.packaging : [{}],
});

// Validates a single flattened (client-shape) Ireland H1 item against exactly the fields shown
// in the table/quick-fix modal. Shared by the bulk validation below and by re-validating a single
// item after it's been fixed via the quick-fix modal.
export const validateIrelandH1Item = async (flatItem: any): Promise<ItemErrors> => {
    try {
        await validateYupSchema(withSeededIrelandH1Packaging(flatItem), irelandH1DisplayedFieldsValidationSchema);
        return {};
    } catch (yupError) {
        return yupToFormErrors(yupError);
    }
};

// After a quick fix an item stays in the table with an empty error object (so the user can see
// the values they just filled in), which means "no issues left" can't be a simple key count.
export const hasAnyItemErrors = (errors: Record<number, ItemErrors>): boolean =>
    Object.values(errors).some((itemErrors) => itemErrors && Object.keys(itemErrors).length > 0);

// Only items with errors are kept - the rest have nothing to fix, so there's no reason to show
// them in the errors table or count them in the progress checklist. Re-indexes errors to match.
export const filterItemsWithErrors = (
    items: any[],
    errorsByOriginalIndex: (ItemErrors | undefined)[]
): Omit<SourceDeclarationItems, 'totalItemCount'> => {
    const filteredItems: any[] = [];
    const errors: Record<number, ItemErrors> = {};
    items.forEach((item, originalIndex) => {
        const itemErrors = errorsByOriginalIndex[originalIndex];
        if (!itemErrors) return;
        errors[filteredItems.length] = itemErrors;
        filteredItems.push(item);
    });
    return { items: filteredItems, errors };
};

export const getIrelandImportH1Items = async (declaration: Declaration): Promise<SourceDeclarationItems> => {
    const goodsItems = declaration.irelandImportDeclaration?.goodsShipment?.goodsShipmentItem ?? [];

    const itemErrorsByOriginalIndex: (ItemErrors | undefined)[] = [];
    for (let i = 0; i < goodsItems.length; i++) {
        const flatItem = IrelandImportH1DeclarationUtils.transformProductForClient(goodsItems[i]);
        const itemErrors = await validateIrelandH1Item(flatItem);

        itemErrorsByOriginalIndex[i] = Object.keys(itemErrors).length > 0 ? itemErrors : undefined;
    }

    return { ...filterItemsWithErrors(goodsItems, itemErrorsByOriginalIndex), totalItemCount: goodsItems.length };
};

// Note: For now we only support mirroring from these source declaration types. Extend when needed.
export const getSourceDeclarationItems = (
    declaration: Declaration,
    { country, internalType, messageType }: NonNullable<MirroringPreparationLocationState['sourceDeclaration']>
): Promise<SourceDeclarationItems> | undefined => {
    if (
        country === DeclarationCountry.IRELAND &&
        internalType === DeclarationInternalType.IMPORT &&
        messageType === AISMessageTypes.H1
    ) {
        return getIrelandImportH1Items(declaration);
    }
    return undefined;
};
